import React, { useRef, useState } from "react";
import { bool, array, string, oneOf } from "prop-types";
import cx from "classnames";
import useClickAway from "../../common/useClickAway.js";
import ShareIcons from "../ShareIcons/ShareIcons.jsx";
import Icon from "../Icon/Icon.jsx";
import useMatchMedia from "../../common/useMatchMedia.js";

const propTypes = {
  dictionaryText: string.isRequired,
  enableCopy: bool.isRequired,
  copyText: string,
  orientation: oneOf(["horizontal", "vertical"]),
  shares: array.isRequired,
  shareCopy: string,
  shareTitle: string,
  defaultExpanded: bool,
  alignment: oneOf(["left", "center"]),
};

let copyTimeoutId;
// From _breakpoints.scss
const desktopMediaQuery = "(min-width: 911px)";

const ShareBar = (props) => {
  const {
    dictionaryText,
    enableCopy,
    copyText,
    orientation,
    alignment,
    shares,
    shareCopy,
    shareTitle,
    defaultExpanded = false,
  } = props;

  const [copied, setCopied] = useState(false);
  const [displayShare, setDisplayShare] = useState(() => {
    const isDesktop = window.matchMedia(desktopMediaQuery).matches;
    if (orientation === "vertical" && !isDesktop) {
      return false;
    } else if (orientation === "vertical" && isDesktop) {
      return true;
    } else {
      return defaultExpanded;
    }
  });

  const isDesktop = useMatchMedia(desktopMediaQuery, (matches) => {
    // For vertical orientation, show on viewport change to desktop,
    // hide on change to mobile
    if (orientation === "vertical") setDisplayShare(matches);
  });

  const shareBarRef = useClickAway({
    isOpen: displayShare,
    setOpen: setDisplayShare,
    disallow: orientation === "vertical" && isDesktop,
  });

  const handleCopy = (e) => {
    async function writeClipboardText(text) {
      try {
        await navigator.clipboard.writeText(text);
      } catch (error) {
        console.error(error.message);
      }
    }

    setCopied(true);
    writeClipboardText(window.location.href);

    if (copyTimeoutId) clearTimeout(copyTimeoutId);
    copyTimeoutId = setTimeout(() => {
      copyTimeoutId = null;
      setCopied(false);
    }, 3000);
  };

  return (
    <div
      ref={shareBarRef}
      className={cx("share-bar", {
        "share-bar--active": displayShare,
        "share-bar--vertical": orientation === "vertical",
        "share-bar--center": alignment === "center",
      })}
    >
      <div className="share-bar__outer">
        {orientation === "horizontal" || !isDesktop ? (
          <button
            aria-expanded={displayShare}
            className="share-bar__toggle"
            onClick={() => setDisplayShare(!displayShare)}
            type="button"
          >
            <span className="share-bar__share-text">{dictionaryText}</span>
            <Icon
              className="icon--24x24 share-bar__share-icon"
              iconName="social-share"
            />
            <span className="u-visuallyhidden">Expand share menu</span>
            <Icon
              className="icon--24x24 share-bar__close-icon"
              iconName="close"
            />
          </button>
        ) : (
          <div className="share-bar__share-text">{dictionaryText}</div>
        )}
      </div>
      <div
        aria-hidden={!displayShare}
        className={cx("share-bar__panel", {
          "share-bar__panel--expanded": displayShare,
        })}
      >
        <div className="share-bar__panel-inner">
          <div className="share-bar__share-icons">
            <ShareIcons
              orientation={orientation}
              shares={shares}
              shareCopy={shareCopy}
              shareTitle={shareTitle}
            />
            {enableCopy && (
              <div className="share-bar__copy-button-container">
                <button
                  onClick={handleCopy}
                  className="share-bar__copy-button share-icons__button"
                  type="button"
                  aria-label={copyText}
                  title={copyText}
                >
                  <Icon className="icon--24x24" iconName="social-copy" />
                  Copy
                </button>
                <div
                  className={cx("share-bar__copied", {
                    "share-bar__copied--is-visibile": copied,
                  })}
                  aria-live="polite"
                >
                  <span>Copied</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ShareBar.propTypes = propTypes;
export default ShareBar;
